import React, { FC } from 'react'
import {
    useDisclosure,
    Modal,
    ModalContent,
    ModalBody,
    Icon,
    Textarea,
    Button,
    Box,
    Image,
    Heading,
    Text,
    ModalCloseButton,
    Link,
} from '@chakra-ui/core'
import { useRecoilValue, useRecoilState } from 'recoil'
import { configState, cartState, orderConfigState } from '../../recoil/atoms'
import { cartStatsState } from '../../recoil/selectors'
import { CartItem } from '../../components/CartItem'
import { LinkButton } from '../../lib/types'
import { Button as ButtonComponent } from '@engyalo/storefront-components'
import { useMutation } from '@apollo/client'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { createOrderMutation } from '../../queries'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import {
    createOrderVariables,
    createOrder_createOrder as ICreateOrder,
} from '@engyalo/storefront-types/lib/graphql/order'
import { OrderStatus } from '@engyalo/storefront-types/lib/graphql/global-types'
import { ExecutionResult } from 'graphql'

export const Cart: FC = () => {
    const { t } = useTranslation()
    const { customer, order } = useParams()
    const config = useRecoilValue(configState)
    const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
    const [cart, setCart] = useRecoilState(cartState)
    const { totalNum, totalPrice } = useRecoilValue(cartStatsState)

    const [message, setMessage] = React.useState('')

    const { isOpen, onOpen, onClose } = useDisclosure()

    const deleteCart = (): void => {
        setCart({})
        onClose()
    }
    const onTextChange = (e: React.FormEvent<HTMLInputElement>): void => {
        setMessage(e.currentTarget.value)
    }

    const [createOrder] = useMutation<ICreateOrder, createOrderVariables>(createOrderMutation, {
        variables: {
            order: {
                storefrontID: config.storefrontId,
                userID: orderConfig.userID || '',
                status: OrderStatus.ENABLE,
                provider: orderConfig.channelName,
                total: parseFloat(totalPrice),
                totalQty: totalNum,
                cart: Object.keys(cart).map((key) => cart[key]),
                note: message,
            },
            configId: order,
        },
        onCompleted: () => {
            if (orderConfig.channelName === 'whatsapp') {
                window.location.href = `http://wa.me/${orderConfig.channelID}`
            }
        },
    })

    return (
        <Box h="100%" w="100%" d="flex" flexDir="column">
            {isOpen && (
                <Box position="absolute" opacity={0.5} backgroundColor="black" width="100%" height="100%" zIndex={50} />
            )}
            <Modal isOpen={isOpen} onClose={onClose} isCentered>
                <ModalContent>
                    <ModalCloseButton background="transparent" borderStyle="none" borderColor="unset" />
                    <ModalBody>
                        <Heading as="h3" fontSize="md" textAlign="center" paddingTop="40px" paddingBottom="16px">
                            {t('cart-removeAllQuestion')}
                            <span role="img" aria-label="scream">
                                😱
                            </span>
                        </Heading>
                        <Box paddingBottom="20px">
                            <ButtonComponent onClick={deleteCart} danger>
                                {t('cart-removeAll')}
                            </ButtonComponent>
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <Box h="40px" d="flex">
                <LinkButton
                    to={`/${customer}/${order}`}
                    h="100%"
                    w="32px"
                    background="transparent"
                    borderColor="transparent"
                    size="lg"
                    aria-label="cart"
                    icon="chevron-left"
                />
                <Image alt="logo" h="36px" alignSelf="center" margin="auto" src={config.logo} />
            </Box>
            <Box padding="12px 12px 8px 16px" borderBottom="1px solid" borderColor="gray.200">
                <Heading as="h2" fontSize="lg" fontWeight="normal">
                    {t('cart-title')}
                </Heading>
                {totalNum > 0 && (
                    <Box display="flex" justifyContent="space-between">
                        <Text fontSize="sm" color="gray.100">
                            {t('cart-resume', { count: totalNum })}
                        </Text>
                        <Button
                            color="primary.500"
                            variant="link"
                            border="transparent"
                            fontSize="sm"
                            textDecor="underline"
                            backgroundColor="transparent"
                            fontWeight="normal"
                            onClick={onOpen}
                        >
                            {t('cart-cleanCart')}
                        </Button>
                    </Box>
                )}
            </Box>
            {/* Body */}
            <Box display="flex" flex="1" flexDir="column">
                {totalNum > 0 ? (
                    Object.keys(cart).map((key) => <CartItem key={key} product={cart[key]} />)
                ) : (
                    <Box display="flex" flexDir="column" justifyContent="center" alignItems="center" h="100%">
                        <Icon name="settings" size="48px" paddingBottom="8px" />
                        <Text fontSize="md" textAlign="center" whiteSpace="pre-line">
                            {t('cart-emptyMessage')}
                        </Text>
                        <Link
                            color="primary.500"
                            href={`/${customer}/${order}`}
                            aria-label="backToCart"
                            border="transparent"
                            fontSize="sm"
                            textDecor="underline"
                            backgroundColor="transparent"
                            fontWeight="normal"
                            paddingTop="32px"
                        >
                            {t('cart-backCatalogue')}
                        </Link>
                    </Box>
                )}
            </Box>
            {/* Body */}
            {totalNum > 0 && config.enabledOrderNote && (
                <Box paddingX="8px">
                    <Text display="inline-flex" fontSize="sm">
                        {t('cart-observations')}
                    </Text>
                    <Text display="inline-flex" paddingLeft="4px" fontSize="sm" color="gray.100">
                        {t('cart-observationsAlter')}
                    </Text>
                    <Textarea
                        marginTop="8px"
                        value={message}
                        onChange={onTextChange}
                        placeholder={t('cart-observationsPlaceholder')}
                        backgroundColor="gray.300"
                        borderColor="gray.200"
                        borderRadius="4px"
                        resize="none"
                        size="sm"
                    />
                </Box>
            )}
            {/* Footer */}
            <Box zIndex={1} paddingY="12px" paddingX="8px" boxShadow="md" backgroundColor="white">
                <Box
                    display="flex"
                    width="100%"
                    paddingY="12px"
                    paddingX="16px"
                    backgroundColor="primary.500"
                    borderColor="transparent"
                    fontWeight="500"
                    color="white"
                    fontFamily="body"
                    fontSize="11px"
                    borderRadius="4px"
                    justifyContent="space-between"
                    as="button"
                    onClick={(): Promise<ExecutionResult<ICreateOrder>> => createOrder()}
                >
                    <Text fontSize="lg" alignSelf="center">
                        {t('cart-pay')}
                    </Text>
                    <Text fontSize="lg" alignSelf="center">{`${config.currencyPrefix} ${totalPrice}`}</Text>
                </Box>
            </Box>
            {/* Footer */}
        </Box>
    )
}
